import { Sale, SalePurpose } from './Sale'
import { Order } from './Order'
import { Address } from './Address'
import { BookingDocket } from './BookingDocket'
import { BookingStatusType } from './BookingStatus'
import { CancellationReason } from './CancellationReason'
import DineroFactory, { Currency, Dinero } from 'dinero.js'

export enum BookingType {
    Standard,
    Event,
}

export class BookingDetails {
    constructor(
        public bookingId: string,
        public currencyCode: string | null,
        public venueId: string,
        public venueName: string,
        public venueAddress: Address,
        public venueDiningInformation: string | null,
        public venueEmailAddress: string | null,
        public venuePhoneNumber: string | null,
        public bookingName: string,
        public bookingEmailAddress: string | null,
        public partySize: number,
        public durationMinutes: number,
        public dateTime: Date,
        public statusType: BookingStatusType,
        public cancellationChargeAmount: number | null,
        public cancellationChargeCutOffTime: Date | null,
        public areaId: string,
        public areaName: string | null,
        public reasonId: string | null,
        public reasonName: string | null,
        public reasonDiningInformation: string | null,
        public reasonIsLinkExclusive: boolean,
        public eventId: string | null,
        public eventName: string | null,
        public eventImageUrl: string | null,
        public depositAmount: number | null,
        public depositAmountPaid: number | null,
        public depositAmountOutstanding: number | null,
        public depositCurrencyCode: string | null,
        public depositDatePaid: Date | null,
        public depositDateRefunded: Date | null,
        public depositRefundCutOffTime: Date | null,
        public depositRefundPolicy: string | null,
        public requiresWheelchairAccess: boolean,
        public requiresDogFriendly: boolean,
        public requiresPramAccessible: boolean,
        public requiresHighchairFriendly: boolean,
        public modifyBookingPath: string | null,
        public pendingPaymentExpiryDateTime: Date | null,
        public docket: BookingDocket | null,
        public cancellationReasons: CancellationReason[],
        public order: Order | null = null,
        public additionalBookings: BookingDetails[] = [],
        public sale: Sale | null = null,
        public orderRefundableLineIds: string[] = [],
        public cancellationAmountCharged: number = 0,
        public cancellationChargeAutomatically: boolean = true,
        public depositAmountRefundable: number | null = null
    ) { }

    public get hasUnpaidOrder(): boolean {
        return this.sale?.purpose === SalePurpose.BookingOrder && !this.sale.payment?.datePaid
    }

    public get upfrontAmountOutstanding(): number {
        return this.hasUnpaidOrder
            ? (this.depositAmountOutstanding ?? 0) + (this.sale?.order.total() ?? 0)
            : this.depositAmountOutstanding ?? this.depositAmount ?? 0
    }

    public orderLineWillBeRefunded(lineId: string): boolean {
        const line = this.sale?.order.lineItems.find(item => item.id === lineId)
        return !!this.sale?.payment?.canRefundOnline && this.orderRefundableLineIds.includes(lineId)
            && !!line?.paidOnline && !!line.customerRefundableUntil && line.customerRefundableUntil.getTime() > Date.now()
            && this.sale.remainingLineAmount(lineId) > 0
    }

    public get orderRefundAmount(): number {
        return this.sale?.order.lineItems.filter(line => this.orderLineWillBeRefunded(line.id))
            .reduce((total, line) => total + this.sale!.remainingLineAmount(line.id), 0) ?? 0
    }

    public get depositRefundAmount(): number {
        return this.depositRefundCutOffTime && this.depositRefundCutOffTime.getTime() > Date.now()
            ? Math.max(0, this.depositAmountRefundable ?? this.depositAmountPaid ?? 0) : 0
    }

    public get cancellationRefundAmount(): number {
        return this.depositRefundAmount + this.orderRefundAmount
    }

    public get retainedPaymentAmount(): number {
        return Math.max(0, (this.sale?.payment?.balance ?? 0) + (this.depositAmountPaid ?? 0) - this.cancellationRefundAmount)
    }

    public get cancellationFeeDue(): number {
        return this.cancellationChargeAutomatically && this.cancellationChargeCutOffTime
            && this.cancellationChargeCutOffTime.getTime() <= Date.now()
            ? Math.max(0, (this.cancellationChargeAmount ?? 0) - this.cancellationAmountCharged) : 0
    }

    public get cancellationCharge(): Dinero | null {
        if (this.cancellationChargeAmount === null) {
            return null
        }
        if (this.currencyCode === null) {
            return null
        }
        return DineroFactory({
            amount: this.cancellationChargeAmount,
            currency: this.currencyCode as Currency,
        })
    }

    public get depositCharge(): Dinero | null {
        if (this.depositAmount === null) {
            return null
        }
        if (this.depositCurrencyCode === null) {
            return null
        }
        return DineroFactory({
            amount: this.depositAmount,
            currency: this.depositCurrencyCode as Currency,
        })
    }

    public get depositPaidCharge(): Dinero | null {
        if (this.depositAmountPaid === null) {
            return null
        }
        if (this.depositCurrencyCode === null) {
            return null
        }
        return DineroFactory({
            amount: this.depositAmountPaid,
            currency: this.depositCurrencyCode as Currency,
        })
    }

    public get endTime(): Date {
        return new Date(this.dateTime.getTime() + this.durationMinutes * 60 * 1000)
    }

    public get isDateRestricted() {
        if (this.modifyBookingPath === null) {
            return false
        }
        const url = new URL(this.modifyBookingPath, window.location.origin)
        const dateRestricted = url.searchParams.get('dateRestricted')
        return dateRestricted === 'true'
    }
}
